import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Check, Star, ArrowRight } from 'lucide-react';

const SIGNUP_URL = 'https://engagement.kolanut.ai/signup';

// Pricing tiers — engagement manager for SaaS founders (matches dashboard app)
const PRICING_TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    who: 'Early-stage founders',
    features: [
      '1 workspace',
      'Auth sync (up to 100 users)',
      'User analytics & lifecycle segments',
      'Onboarding signal discovery (3 signals)',
      'AI engagement assistant (limited)',
      'Company Brain setup',
      '5 AI email recommendations/month',
      'Community support',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    popular: true,
    price: 49,
    trialDays: 7,
    who: 'Active SaaS teams',
    features: [
      'Everything in Free',
      'Unlimited synced auth users',
      'Full onboarding signals & drop-off tracking',
      'AI email recommendations & campaign drafts',
      'Outreach campaigns (unlimited email)',
      'Accounts explorer & user metadata',
      'Auth & database integrations',
      'AI engagement copilot',
      'Engagement plays (Saves & Expansion)',
      'Priority email support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 99,
    who: 'Scaling products',
    features: [
      'Everything in Growth',
      '3 workspaces',
      'Advanced segment targeting',
      'Unlimited onboarding signals',
      'Campaign template library + AI drafts',
      '500 SMS/month for outreach',
      'Paid ads performance insights',
      'Support agent inbox',
      'Advanced engagement analytics',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 199,
    who: 'Agencies & large teams',
    features: [
      'Everything in Pro',
      'Unlimited workspaces',
      'Unlimited SMS outreach',
      'Custom onboarding signal rules',
      'Dedicated account manager',
      'Custom integrations & API access',
      'Team collaboration & roles',
      'White-label options',
      'Priority support (24/7)',
      'Custom AI training on your product',
    ],
  },
];

const PricingPage = () => {
  const [selectedTier, setSelectedTier] = useState(null);

  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
  };

  const handleGetStarted = (tier) => {
    const params = new URLSearchParams({ plan: tier.id });
    window.location.href = `${SIGNUP_URL}?${params.toString()}`;
  };

  const ctaLabel = (tier) => {
    if (tier.id === 'free') return `Get ${tier.name} free`;
    if (tier.id === 'growth') return 'Start 7-day free trial';
    return `Subscribe to ${tier.name}`;
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <main className="flex-grow">
        <div className="min-h-screen bg-white py-16 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-[95%] lg:max-w-[98%] xl:max-w-full">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center mb-8">
                <div className="rounded-2xl bg-gray-100 border border-gray-200 shadow-sm">
                  <img
                    src="/logo512.png"
                    alt="Kolanut"
                    className="w-16 h-16 object-contain rounded-xl"
                  />
                </div>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                Choose your plan
              </h1>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                AI engagement management for SaaS founders — track user health, fix onboarding drop-offs, and send targeted outreach. Start{' '}
                <span className="font-semibold text-gray-900">Free</span> or try{' '}
                <span className="font-semibold text-gray-900">Growth with a 7-day trial</span>.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6 mb-16 w-full">
              {PRICING_TIERS.map((tier) => (
                <div
                  key={tier.id}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleTierSelect(tier);
                    }
                  }}
                  className={`relative bg-white rounded-3xl p-8 border transition-all cursor-pointer h-full flex flex-col ${
                    selectedTier?.id === tier.id
                      ? 'border-gray-900 shadow-2xl ring-4 ring-gray-900 ring-opacity-5'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                  } ${tier.popular ? 'lg:scale-105' : ''}`}
                  onClick={() => handleTierSelect(tier)}
                >
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gray-900 text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                        <Star className="w-3 h-3 fill-white" />
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tier.name}</h3>
                    <div className="flex items-baseline mb-3">
                      {tier.price === 0 ? (
                        <span className="text-4xl font-bold text-gray-900">Free</span>
                      ) : (
                        <>
                          <span className="text-4xl font-bold text-gray-900">${tier.price}</span>
                          <span className="text-gray-500 ml-2 text-sm">/month</span>
                        </>
                      )}
                    </div>
                    {tier.id === 'growth' && (
                      <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full border border-blue-200">
                        <span>✓</span>
                        7-day free trial
                      </div>
                    )}
                    {tier.id === 'free' && (
                      <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium px-3 py-1.5 rounded-full border border-emerald-200">
                        <span>✓</span>
                        Forever free
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-gray-500 mb-6">{tier.who}</p>

                  <ul className="space-y-3 mb-8 flex-grow">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2.5">
                        <Check className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className={`mt-auto w-full py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                      selectedTier?.id === tier.id
                        ? 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTierSelect(tier);
                    }}
                  >
                    {selectedTier?.id === tier.id ? 'Selected' : 'Select plan'}
                  </button>
                </div>
              ))}
            </div>

            {selectedTier && (
              <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl border border-gray-200 max-w-2xl mx-auto mb-16">
                <div className="text-center mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 tracking-tight">
                    {selectedTier.id === 'free' ? 'Confirm free access' : 'Ready to get started?'}
                  </h2>
                  <p className="text-gray-500">
                    {selectedTier.id === 'free' ? (
                      <>
                        <span className="font-medium text-gray-900">{selectedTier.name}</span> plan —{' '}
                        <span className="font-semibold text-emerald-600">Free</span>
                      </>
                    ) : (
                      <>
                        <span className="font-medium text-gray-900">{selectedTier.name}</span> plan —{' '}
                        <span className="font-semibold text-gray-900">${selectedTier.price}/month</span>
                        {selectedTier.id === 'growth' && (
                          <span className="text-gray-500"> • 7-day free trial</span>
                        )}
                      </>
                    )}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => handleGetStarted(selectedTier)}
                  className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-gray-800 font-medium transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {ctaLabel(selectedTier)}
                  <ArrowRight className="w-5 h-5" />
                </button>

                {selectedTier.id === 'growth' && (
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    Your card won&apos;t be charged until the trial ends.
                  </p>
                )}
              </div>
            )}

            <div className="text-center mt-16 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                By continuing, you agree to our{' '}
                <a href="/terms-of-use" className="text-gray-900 hover:underline font-medium">Terms of Service</a>{' '}
                and{' '}
                <a href="/privacy-policy" className="text-gray-900 hover:underline font-medium">Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PricingPage;
